import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { BASE_URL } from "../../data/BaseUrl";
import "react-toastify/dist/ReactToastify.css";

const DENOMINATIONS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

const CashVerificationPage = () => {
  const { token } = useParams();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Photo state
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Denomination state
  const [denomCounts, setDenomCounts] = useState(
    DENOMINATIONS.reduce((acc, d) => ({ ...acc, [d]: 0 }), {})
  );

  const totalAmount = DENOMINATIONS.reduce(
    (sum, d) => sum + d * (denomCounts[d] || 0),
    0
  );

  // Fetch session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await fetch(`${BASE_URL}/cash-verify/session/${token}`);
        const data = await res.json();

        if (data?.status) {
          if (data.data?.status === "verified") {
            setSubmitted(true);
          }
          setSessionData(data.data);
        } else {
          setError(data?.message || "Invalid verification link");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load verification details");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchSession();
  }, [token]);

  // Check if getUserMedia is available (requires HTTPS or localhost)
  useEffect(() => {
    const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    setCameraSupported(isSecure && hasGetUserMedia);
  }, []);

  // File input handler (fallback for non-HTTPS / IP address access)
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setCapturedImage(ev.target.result);
    };
    reader.readAsDataURL(file);
    setCapturedBlob(file);
  };

  // Camera handling
  const startCamera = async () => {
    if (!cameraSupported) {
      // Fallback: open file input with camera capture
      fileInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setShowCamera(true);
      // Wait for video element to mount, then assign stream and play
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((e) => console.error("Video play error:", e));
        }
      }, 100);
    } catch (err) {
      console.error("Camera error:", err);
      // Fallback to file input if camera fails
      fileInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedImage(dataUrl);

    canvas.toBlob(
      (blob) => {
        setCapturedBlob(blob);
      },
      "image/jpeg",
      0.8
    );

    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleDenomChange = (denom, value) => {
    const count = Math.max(0, parseInt(value) || 0);
    setDenomCounts((prev) => ({ ...prev, [denom]: count }));
  };

  const handleSubmit = async () => {
    if (!capturedBlob) {
      toast.error("Please capture a photo of the cash");
      return;
    }

    if (totalAmount === 0) {
      toast.error("Please enter denominations");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("image", capturedBlob, "cash-photo.jpg");
      formData.append(
        "denominations",
        JSON.stringify(
          DENOMINATIONS.filter((d) => denomCounts[d] > 0).map((d) => ({
            denomination: d,
            count: denomCounts[d],
            subtotal: d * denomCounts[d],
          }))
        )
      );
      formData.append("total_amount", totalAmount.toString());

      const res = await fetch(`${BASE_URL}/cash-verify/submit/${token}`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data?.status) {
        setSubmitted(true);
        toast.success("Cash verified successfully!");
      } else {
        toast.error(data?.message || "Verification failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>&#10060;</div>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successIcon}>&#10003;</div>
          <h2 style={styles.successTitle}>Cash Verified!</h2>
          <p style={styles.successText}>
            The cash has been verified. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" autoClose={3000} />
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Cash Verification</h2>
          <p style={styles.headerSub}>WTF Powered Gyms</p>
        </div>

        {/* Member Info */}
        <div style={styles.infoBox}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Member</span>
            <span style={styles.infoValue}>{sessionData?.member_name}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Gym</span>
            <span style={styles.infoValue}>{sessionData?.gym_name}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Amount</span>
            <span style={{ ...styles.infoValue, fontWeight: "700", color: "#28a745" }}>
              Rs. {sessionData?.price}
            </span>
          </div>
        </div>

        {/* Camera Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>1. Cash Photo</h3>

          {/* Hidden file input for camera fallback (HTTP/IP access) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {!capturedImage && !showCamera && (
            <button onClick={startCamera} style={styles.cameraBtn}>
              {cameraSupported ? "Open Camera" : "Take Photo"}
            </button>
          )}

          {showCamera && (
            <div style={styles.cameraContainer}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={styles.video}
              />
              <button onClick={capturePhoto} style={styles.captureBtn}>
                Capture
              </button>
              <button onClick={stopCamera} style={styles.cancelBtn}>
                Cancel
              </button>
            </div>
          )}

          {capturedImage && (
            <div style={styles.previewContainer}>
              <img
                src={capturedImage}
                alt="Cash"
                style={styles.previewImage}
              />
              <button onClick={retakePhoto} style={styles.retakeBtn}>
                Retake
              </button>
            </div>
          )}
        </div>

        {/* Denominations Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>2. Denominations</h3>

          <div style={styles.denomGrid}>
            {DENOMINATIONS.map((d) => (
              <div key={d} style={styles.denomRow}>
                <span style={styles.denomLabel}>Rs. {d}</span>
                <div style={styles.denomControls}>
                  <button
                    style={styles.denomBtn}
                    onClick={() =>
                      handleDenomChange(d, (denomCounts[d] || 0) - 1)
                    }
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={denomCounts[d] || ""}
                    onChange={(e) => handleDenomChange(d, e.target.value)}
                    style={styles.denomInput}
                    placeholder="0"
                  />
                  <button
                    style={styles.denomBtn}
                    onClick={() =>
                      handleDenomChange(d, (denomCounts[d] || 0) + 1)
                    }
                  >
                    +
                  </button>
                </div>
                <span style={styles.denomSubtotal}>
                  {denomCounts[d] > 0
                    ? `Rs. ${d * denomCounts[d]}`
                    : "-"}
                </span>
              </div>
            ))}
          </div>

          <div style={styles.totalRow}>
            <span style={styles.totalLabel}>Total</span>
            <span style={styles.totalValue}>Rs. {totalAmount.toLocaleString("en-IN")}</span>
          </div>

          {sessionData?.price && totalAmount > 0 && totalAmount !== Number(sessionData.price) && (
            <p style={styles.mismatchWarning}>
              Total (Rs. {totalAmount.toLocaleString("en-IN")}) does not match expected amount (Rs. {Number(sessionData.price).toLocaleString("en-IN")})
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !capturedBlob || totalAmount === 0}
          style={{
            ...styles.submitBtn,
            opacity: submitting || !capturedBlob || totalAmount === 0 ? 0.5 : 1,
          }}
        >
          {submitting ? "Verifying..." : "Verify Cash"}
        </button>
      </div>
    </div>
  );
};

const styles = {
  page: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#f0f2f5",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "16px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    backgroundColor: "#fff",
    borderRadius: "16px",
    overflow: "visible",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    marginBottom: "32px",
  },
  header: {
    backgroundColor: "#1a1a2e",
    padding: "24px 20px",
    textAlign: "center",
  },
  headerTitle: {
    color: "#fff",
    fontSize: "22px",
    fontWeight: "700",
    margin: "0",
  },
  headerSub: {
    color: "#aaa",
    fontSize: "13px",
    margin: "4px 0 0",
  },
  infoBox: {
    padding: "16px 20px",
    borderBottom: "1px solid #eee",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
  },
  infoLabel: {
    color: "#888",
    fontSize: "14px",
  },
  infoValue: {
    color: "#333",
    fontSize: "14px",
    fontWeight: "500",
  },
  section: {
    padding: "20px",
    borderBottom: "1px solid #eee",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    marginTop: "0",
  },
  cameraBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
  cameraContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  video: {
    width: "100%",
    borderRadius: "10px",
    backgroundColor: "#000",
  },
  captureBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
  cancelBtn: {
    width: "100%",
    padding: "10px",
    backgroundColor: "transparent",
    color: "#dc3545",
    border: "1px solid #dc3545",
    borderRadius: "10px",
    fontSize: "14px",
    cursor: "pointer",
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  previewImage: {
    width: "100%",
    borderRadius: "10px",
    maxHeight: "300px",
    objectFit: "cover",
  },
  retakeBtn: {
    padding: "10px 24px",
    backgroundColor: "transparent",
    color: "#007bff",
    border: "1px solid #007bff",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  denomGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  denomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  denomLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#333",
    minWidth: "70px",
  },
  denomControls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  denomBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    fontSize: "18px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#333",
  },
  denomInput: {
    width: "50px",
    textAlign: "center",
    padding: "6px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "15px",
  },
  denomSubtotal: {
    fontSize: "13px",
    color: "#666",
    minWidth: "80px",
    textAlign: "right",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
    padding: "14px 12px",
    backgroundColor: "#e8f5e9",
    borderRadius: "10px",
  },
  totalLabel: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#333",
  },
  totalValue: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#28a745",
  },
  mismatchWarning: {
    marginTop: "8px",
    padding: "10px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderRadius: "8px",
    fontSize: "13px",
    textAlign: "center",
  },
  submitBtn: {
    width: "calc(100% - 40px)",
    margin: "20px",
    padding: "16px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
  },
  loadingText: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
    fontSize: "16px",
  },
  errorIcon: {
    textAlign: "center",
    fontSize: "48px",
    marginTop: "30px",
  },
  errorText: {
    textAlign: "center",
    padding: "20px 20px 40px",
    color: "#dc3545",
    fontSize: "16px",
  },
  successIcon: {
    textAlign: "center",
    fontSize: "64px",
    color: "#28a745",
    marginTop: "40px",
  },
  successTitle: {
    textAlign: "center",
    color: "#28a745",
    fontSize: "24px",
    marginBottom: "8px",
  },
  successText: {
    textAlign: "center",
    color: "#666",
    fontSize: "14px",
    padding: "0 20px 40px",
  },
};

export default CashVerificationPage;
